import {
  Camera,
  CameraOff,
  CheckSquare2,
  Clock3,
  Globe2,
  LayoutGrid,
  LogOut,
  MessageCircle,
  MicOff,
  Minus,
  Music2,
  Pause,
  PictureInPicture2,
  Play,
  Plus,
  Settings,
  Sparkles,
  Users,
  Video,
  VideoOff,
  Volume2,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ClientIdentity, Intention, UserProfile } from '../shared/types'
import { SESSION_BADGES, badgeForSessions, badgeLabel } from '../shared/types'
import { AuthGate } from './components/AuthGate'
import { JoinGate } from './components/JoinGate'
import { GlobePanel } from './components/GlobePanel'
import { SettingsDialog } from './components/SettingsDialog'
import {
  CameraOffDialog,
  ChatPanel,
  IntentionsPanel,
  PeoplePanel,
  SoundsPanel,
  type PanelName,
} from './components/Panels'
import { SessionBadge, VideoGrid } from './components/VideoGrid'

/** FLOWN-style milestone celebration copy; each rung gets its own moment. */
function celebrationCopy(badge: number): { title: string; text: string } {
  const label = badgeLabel(badge)
  const next = SESSION_BADGES.find((step) => step > badge)
  if (badge === 1) return { title: 'First session! 🎉', text: 'Showing up is the hardest part — and you just did it.' }
  if (badge === 3) return { title: 'Three sessions! 🌱', text: 'This is how a habit starts. Keep the momentum.' }
  if (badge === 5) return { title: 'High five! 🖐️', text: 'Five sessions in — the room is starting to know you.' }
  if (badge === 10) return { title: 'Double digits! 🔟', text: '10 sessions is where it starts to stick — 30 is closer than you think.' }
  return {
    title: `${label} sessions! 🏅`,
    text: next
      ? `${label} sessions of focused work. Next stop: ${badgeLabel(next)}.`
      : `${label} sessions of focused work. You are the room.`,
  }
}
import { useCamera } from './hooks/useCamera'
import { useAuth } from './hooks/useAuth'
import { useDailyVideo } from './hooks/useDailyVideo'
import { useMeshVideo } from './hooks/useMeshVideo'
import { useRoom } from './hooks/useRoom'
import { useTimer } from './hooks/useTimer'
import { createIdentity, loadIdentity } from './lib/identity'
import { soundEngine } from './lib/soundEngine'

// Development-only UI fixtures. import.meta.env.DEV is false in production builds,
// so fabricated people can never appear for real users.
const DEMO_USERS: UserProfile[] = import.meta.env.DEV
  ? ([
      ['Maya', 'Product designer · London', '#d26d93', 225, 51.5072, -0.1276, 'London'],
      ['Theo', 'Writer · Lisbon', '#557bd3', 90, 38.7223, -9.1393, 'Lisbon'],
      ['Amara', 'Researcher · Berlin', '#bd7351', 60, 52.52, 13.405, 'Berlin'],
      ['Noah', 'Founder · Zurich', '#4d9a79', 40, 47.3769, 8.5417, 'Zurich'],
      ['Lina', 'PhD student', '#8c67c8', 30, 48.8566, 2.3522, 'Paris'],
      ['Sam', 'Independent developer', '#c95e59', 15, 37.7749, -122.4194, 'San Francisco'],
      ['Emi', 'Illustrator · Tokyo', '#4f88aa', 10, 35.6762, 139.6503, 'Tokyo'],
      ['Jon', 'Learning in public', '#8c9146', 5, 55.6761, 12.5683, 'Copenhagen'],
      ['Iris', 'Consultant · Amsterdam', '#9c62a9', 1, 52.3676, 4.9041, 'Amsterdam'],
      ['Alex', '', '#597390', 125, -33.8688, 151.2093, 'Sydney'],
      ['Nia', 'Building something new', '#b76870', 25, -33.9249, 18.4241, 'Cape Town'],
    ] as const).map(([name, role, color, sessions, latitude, longitude, location], index) => ({
      id: `demo-${index}`,
      name: String(name),
      role: String(role),
      color: String(color),
      sessions: Number(sessions),
      latitude: Number(latitude),
      longitude: Number(longitude),
      location: String(location),
      cameraOn: index !== 7,
      joinedAt: new Date(Date.now() - index * 120_000).toISOString(),
      // muted Pixabay work loops (public/demo/LICENSE.md) so the demo room
      // feels alive; only some tiles, the rest keep avatar placeholders
      demoVideoUrl: index !== 7 && index < 8 ? `/demo/demo-${(index % 6) + 1}.mp4` : undefined,
    }))
  : []

/** Dev-only intentions so the panel shows how a lived-in room feels. */
const DEMO_INTENTIONS: Intention[] = import.meta.env.DEV
  ? [
      {
        id: 'demo-intention-1',
        userId: 'demo-0',
        userName: 'Maya',
        userRole: 'Product designer',
        userColor: '#d26d93',
        title: 'Finish the onboarding flow specs',
        steps: [
          { id: 'demo-s1', text: 'Empty states', completed: true },
          { id: 'demo-s2', text: 'Error states', completed: false },
        ],
        isPublic: true,
        status: 'in_progress',
        durationMinutes: 50,
        createdAt: new Date(Date.now() - 22 * 60_000).toISOString(),
        reactions: { '❤️': ['demo-3', 'demo-5'] },
      },
      {
        id: 'demo-intention-2',
        userId: 'demo-3',
        userName: 'Noah',
        userRole: 'Founder',
        userColor: '#4d9a79',
        title: 'Investor update draft',
        steps: [],
        isPublic: true,
        status: 'completed',
        createdAt: new Date(Date.now() - 65 * 60_000).toISOString(),
        completedAt: new Date(Date.now() - 9 * 60_000).toISOString(),
        reactions: { '❤️': ['demo-0'] },
      },
      {
        id: 'demo-intention-3',
        userId: 'demo-1',
        userName: 'Theo',
        userRole: 'Writer',
        userColor: '#557bd3',
        title: 'Chapter 4, two pomodoros',
        steps: [],
        isPublic: true,
        status: 'in_progress',
        createdAt: new Date(Date.now() - 6 * 60_000).toISOString(),
        reactions: {},
      },
    ]
  : []

const HIDDEN_KEY = 'focus-room.hidden.v1'

function playCompletionTone() {
  const context = new AudioContext()
  const gain = context.createGain()
  gain.gain.setValueAtTime(0.0001, context.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.16, context.currentTime + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 1.3)
  gain.connect(context.destination)
  ;[523.25, 659.25, 783.99].forEach((frequency, index) => {
    const oscillator = context.createOscillator()
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    oscillator.connect(gain)
    oscillator.start(context.currentTime + index * 0.12)
    oscillator.stop(context.currentTime + 1.35)
  })
  window.setTimeout(() => void context.close(), 1600)
}

function ToolbarButton({
  active,
  icon,
  label,
  onClick,
}: {
  active?: boolean
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button className={`toolbar-button ${active ? 'active' : ''}`} onClick={onClick}>
      <span>{icon}</span>
      <small>{label}</small>
    </button>
  )
}

export function App() {
  const searchParams = new URLSearchParams(window.location.search)
  const previewMode = import.meta.env.DEV && searchParams.get('preview') === '1'
  const previewLobby = previewMode && searchParams.get('lobby') === '1'
  const demoMode = import.meta.env.DEV && searchParams.get('demo') === '1'
  const auth = useAuth()
  const [previewIdentity] = useState<ClientIdentity | null>(() =>
    previewMode
      ? loadIdentity() ?? createIdentity('Preview', '')
      : null,
  )
  const identity = previewMode ? previewIdentity : auth.user
  const [joined, setJoined] = useState(previewMode && !previewLobby)
  const [activePanel, setActivePanel] = useState<PanelName | null>(null)
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() =>
    window.matchMedia('(max-width: 620px)').matches ? 3 : 18,
  )
  const [cameraPrompt, setCameraPrompt] = useState(false)
  const [cameraPromptSeconds, setCameraPromptSeconds] = useState(120)
  const [celebration, setCelebration] = useState<number | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [view, setView] = useState<'tiles' | 'globe'>('tiles')
  const [timerPopoverOpen, setTimerPopoverOpen] = useState(false)
  const [focusMinutes, setFocusMinutes] = useState(0)
  const [pinnedIds, setPinnedIds] = useState<ReadonlySet<string>>(new Set())
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]') as string[])
    } catch {
      return new Set()
    }
  })
  const [toast, setToast] = useState<{ text: string; key: number } | null>(null)
  const [pipActive, setPipActive] = useState(false)
  const cameraNudgeTimeout = useRef<number | null>(null)
  const toastTimeout = useRef<number | null>(null)
  const pipCleanup = useRef<(() => void) | null>(null)
  const joinedAt = useRef(Date.now())
  const camera = useCamera()
  const room = useRoom(joined ? identity : null, previewMode)
  const mediaProvider =
    import.meta.env.VITE_MEDIA_PROVIDER === 'mesh' ? 'mesh' : 'daily'
  const timerComplete = useCallback(() => playCompletionTone(), [])
  const timer = useTimer(timerComplete)

  const showToast = useCallback((text: string) => {
    setToast({ text, key: Date.now() })
    if (toastTimeout.current) window.clearTimeout(toastTimeout.current)
    toastTimeout.current = window.setTimeout(() => setToast(null), 1_900)
  }, [])

  const currentUser = useMemo<UserProfile | null>(() => {
    if (!identity) return null
    return (
      room.users.find((user) => user.id === identity.id) ?? {
        ...identity,
        sessions: 1,
        cameraOn: camera.state === 'ready' && camera.cameraOn,
        joinedAt: new Date().toISOString(),
      }
    )
  }, [camera.cameraOn, camera.state, identity, room.users])

  // People panel sees everyone; the grid additionally drops locally hidden users
  // and keeps pinned users directly after the local tile.
  const displayedUsers = useMemo<Array<UserProfile & { isLocal?: boolean }>>(() => {
    if (!currentUser) return []
    const remote = room.users.filter((user) => user.id !== currentUser.id)
    const demos = demoMode
      ? DEMO_USERS.filter((demo) => !remote.some((user) => user.id === demo.id))
      : []
    return [{ ...currentUser, isLocal: true }, ...remote, ...demos]
  }, [currentUser, demoMode, room.users])

  // preview mode skips the join gate, so nothing ever started the camera —
  // start it here or the local room only ever shows placeholders
  useEffect(() => {
    if (previewMode && joined) void camera.start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode, joined])

  // FLOWN-style milestone moment: celebrate each newly reached badge once
  useEffect(() => {
    if (!joined || !identity) return
    const badge = badgeForSessions(currentUser?.sessions ?? 0)
    if (!badge) return
    const key = `focus-room.badge-seen.${identity.id}`
    let seen = 0
    try { seen = Number(localStorage.getItem(key) || 0) } catch { /* private mode */ }
    if (badge <= seen) return
    try { localStorage.setItem(key, String(badge)) } catch { /* private mode */ }
    setCelebration(badge)
  }, [joined, currentUser?.sessions, identity])

  const gridUsers = useMemo(() => {
    const others = displayedUsers.filter((user) => !user.isLocal && !hiddenIds.has(user.id))
    const pinned = others.filter((user) => pinnedIds.has(user.id))
    const rest = others.filter((user) => !pinnedIds.has(user.id))
    return [...displayedUsers.filter((user) => user.isLocal), ...pinned, ...rest]
  }, [displayedUsers, hiddenIds, pinnedIds])

  const visibleIdentityIds = useMemo(() => {
    // Clamp here too: the state clamp lands one render later.
    const totalPages = Math.max(1, Math.ceil(gridUsers.length / pageSize))
    const safePage = Math.min(page, totalPages - 1)
    return gridUsers
      .slice(safePage * pageSize, safePage * pageSize + pageSize)
      .filter((user) => !user.isLocal && user.socketId)
      .map((user) => user.id)
  }, [gridUsers, page, pageSize])

  const daily = useDailyVideo(
    mediaProvider === 'daily' && joined && !previewMode,
    identity,
    camera.stream,
    camera.cameraOn,
    visibleIdentityIds,
  )
  const meshStreams = useMeshVideo(
    mediaProvider === 'mesh',
    room.socket,
    camera.stream,
    room.users,
  )
  const remoteStreams = mediaProvider === 'daily' ? daily.remoteStreams : meshStreams

  const globeUsers = useMemo(
    () => displayedUsers.filter(
      (user) => user.id === identity?.id || user.profilePublic !== false,
    ),
    [displayedUsers, identity?.id],
  )

  useEffect(() => {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hiddenIds]))
  }, [hiddenIds])

  useEffect(() => {
    if (joined) joinedAt.current = Date.now()
  }, [joined])

  useEffect(() => {
    if (!timerPopoverOpen) return
    const update = () => setFocusMinutes(Math.max(0, Math.floor((Date.now() - joinedAt.current) / 60_000)))
    update()
    const interval = window.setInterval(update, 30_000)
    return () => window.clearInterval(interval)
  }, [timerPopoverOpen])

  useEffect(() => {
    // Keep the page in range when people leave; otherwise tiles lose their video feed.
    const totalPages = Math.max(1, Math.ceil(gridUsers.length / pageSize))
    setPage((current) => Math.min(current, totalPages - 1))
  }, [gridUsers.length, pageSize])

  useEffect(() => {
    const media = window.matchMedia('(max-width: 620px)')
    const resizePage = () => {
      setPageSize(media.matches ? 3 : 16)
      setPage(0)
    }
    media.addEventListener('change', resizePage)
    return () => media.removeEventListener('change', resizePage)
  }, [])

  useEffect(() => {
    if (!joined) return
    room.updatePresence({
      cameraOn: camera.state === 'ready' && camera.cameraOn,
      media: { provider: mediaProvider },
    })
  }, [camera.cameraOn, camera.state, joined, mediaProvider, room.updatePresence])

  useEffect(() => {
    if (!cameraPrompt) return
    setCameraPromptSeconds(120)
    const interval = window.setInterval(() => {
      setCameraPromptSeconds((current) => {
        if (current <= 1) {
          window.clearInterval(interval)
          setCameraPrompt(false)
          return 0
        }
        return current - 1
      })
    }, 1000)
    return () => window.clearInterval(interval)
  }, [cameraPrompt])

  useEffect(() => () => {
    if (cameraNudgeTimeout.current) window.clearTimeout(cameraNudgeTimeout.current)
    if (toastTimeout.current) window.clearTimeout(toastTimeout.current)
    pipCleanup.current?.()
  }, [])

  const scheduleCameraNudge = () => {
    if (cameraNudgeTimeout.current) window.clearTimeout(cameraNudgeTimeout.current)
    cameraNudgeTimeout.current = window.setTimeout(() => setCameraPrompt(true), 12_000)
  }

  const handleJoin = async () => {
    // getUserMedia can stall indefinitely (permission prompt left open,
    // camera held by another app); never let that keep someone out of the
    // room — the camera attaches on its own once it resolves
    await Promise.race([
      camera.start().catch(() => undefined),
      new Promise((resolve) => window.setTimeout(resolve, 4_000)),
    ])
    setJoined(true)
  }

  const leaveRoom = () => {
    pipCleanup.current?.()
    soundEngine.stopAll()
    setActivePanel(null)
    setSelectedUser(null)
    setTimerPopoverOpen(false)
    setJoined(false)
  }

  const togglePanel = (panel: PanelName) => {
    setSelectedUser(null)
    setActivePanel((current) => (current === panel ? null : panel))
  }

  const sidePanelOpen = activePanel !== null

  const selectUser = (user: UserProfile) => {
    setSelectedUser(user)
    setActivePanel('people')
  }

  const togglePin = (userId: string) => {
    setPinnedIds((current) => {
      const next = new Set(current)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const toggleHidden = (userId: string) => {
    const name = displayedUsers.find((user) => user.id === userId)?.name ?? 'User'
    setHiddenIds((current) => {
      const next = new Set(current)
      if (next.has(userId)) {
        next.delete(userId)
        showToast(`${name} is visible again`)
      } else {
        next.add(userId)
        showToast(`${name} hidden`)
      }
      return next
    })
  }

  const reportUser = (userId: string) => {
    room.reportUser(userId)
    showToast('Reported')
  }

  const toggleCamera = async () => {
    if (camera.state !== 'ready') {
      if (previewMode) {
        camera.setEnabled(false)
        scheduleCameraNudge()
        return
      }
      await camera.start()
      return
    }
    if (camera.cameraOn) {
      camera.setEnabled(false)
      scheduleCameraNudge()
    } else {
      if (cameraNudgeTimeout.current) window.clearTimeout(cameraNudgeTimeout.current)
      camera.setEnabled(true)
    }
  }

  // Picture-in-picture shows the room, not just yourself: the visible tiles are
  // composited onto a canvas and that stream goes into the floating window.
  const togglePiP = async () => {
    if (!document.pictureInPictureEnabled) return
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture().catch(() => undefined)
      return
    }
    const tiles = [...document.querySelectorAll<HTMLVideoElement>('.video-tile video')]
      .filter((video) => video.srcObject && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)
      .slice(0, 9)
    if (!tiles.length) return
    const canvas = document.createElement('canvas')
    canvas.width = 1280
    canvas.height = 720
    const context = canvas.getContext('2d')
    if (!context) return
    const columns = tiles.length <= 1 ? 1 : tiles.length <= 4 ? 2 : 3
    const rows = Math.ceil(tiles.length / columns)
    const draw = () => {
      context.fillStyle = '#111217'
      context.fillRect(0, 0, canvas.width, canvas.height)
      tiles.forEach((video, index) => {
        const width = canvas.width / columns
        const height = canvas.height / rows
        const x = (index % columns) * width
        const y = Math.floor(index / columns) * height
        try {
          context.drawImage(video, x + 2, y + 2, width - 4, height - 4)
        } catch {
          // a tile can disappear while PiP is open
        }
      })
    }
    draw()
    const interval = window.setInterval(draw, 100)
    const pipVideo = document.createElement('video')
    pipVideo.muted = true
    pipVideo.playsInline = true
    pipVideo.srcObject = canvas.captureStream(10)
    Object.assign(pipVideo.style, { position: 'fixed', width: '2px', opacity: '0', pointerEvents: 'none' })
    document.body.appendChild(pipVideo)
    const cleanup = () => {
      window.clearInterval(interval)
      pipVideo.remove()
      pipCleanup.current = null
      setPipActive(false)
    }
    pipCleanup.current = cleanup
    pipVideo.addEventListener('leavepictureinpicture', cleanup, { once: true })
    try {
      await pipVideo.play()
      await pipVideo.requestPictureInPicture()
      setPipActive(true)
    } catch {
      cleanup()
    }
  }

  const localDisplayStream = daily.localStream ?? camera.stream
  const pipAvailable =
    typeof document !== 'undefined' &&
    document.pictureInPictureEnabled &&
    Boolean(localDisplayStream || remoteStreams.size)

  if (!previewMode && auth.loading) {
    return <div className="auth-loading"><span className="wordmark">FOCUS</span></div>
  }

  if (!identity) {
    return (
      <AuthGate
        requestCode={auth.requestCode}
        verifyCode={auth.verifyCode}
        googleClientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}
        turnstileSiteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
        onGoogle={auth.loginWithGoogle}
      />
    )
  }

  if (!joined) {
    return (
      <>
        <JoinGate
          identity={identity}
          cameraState={camera.state}
          stream={camera.stream}
          cameraOn={camera.cameraOn}
          onStartCamera={camera.start}
          onSetCamera={camera.setEnabled}
          onOpenSettings={() => setSettingsOpen(true)}
          onJoin={handleJoin}
          onLogout={() => void auth.logout()}
          previewMode={previewMode}
        />
        {settingsOpen && (
          <SettingsDialog
            stream={camera.stream}
            devices={camera.devices}
            deviceId={camera.deviceId}
            backgroundEffect={daily.backgroundEffect}
            effectsAvailable={false}
            onSelectDevice={camera.selectDevice}
            onBackgroundEffect={daily.applyBackground}
            onClose={() => setSettingsOpen(false)}
          />
        )}
      </>
    )
  }

  if (!currentUser) return null

  return (
    <div className="room-app">
      <header className="room-window-bar">
        <div className="window-brand"><span className="brand-mark"><Sparkles size={14} /></span><strong>FOCUS</strong></div>
        <div
          className={`connection-pill ${
            room.connected &&
            (previewMode || mediaProvider === 'mesh' || daily.state === 'joined')
              ? 'online'
              : ''
          }`}
          title={daily.error || undefined}
        >
          <span />
          {!room.connected
            ? 'Reconnecting'
            : previewMode || mediaProvider === 'mesh' || daily.state === 'joined'
              ? 'Live'
              : daily.state === 'error'
                ? 'Video offline'
                : 'Joining video'}
        </div>
        <div className="window-actions" />
      </header>

      {toast && <div className="app-toast" key={toast.key}>{toast.text}</div>}

      <main className={`room-main ${sidePanelOpen ? 'panel-open' : ''}`}>
        <div className="top-tools">
          <div className="top-tools-left">
            <button className="ghost-tool" title="Settings" onClick={() => setSettingsOpen(true)}><Settings /></button>
          </div>
          <div className="compact-timer-anchor">
            <div className="stage-switch" role="tablist" aria-label="Stage view">
              <button
                role="tab"
                aria-selected={view === 'tiles'}
                className={view === 'tiles' ? 'active' : ''}
                title="Room tiles"
                aria-label="Room tiles"
                onClick={() => setView('tiles')}
              >
                <LayoutGrid size={17} />
              </button>
              <button
                role="tab"
                aria-selected={view === 'globe'}
                className={view === 'globe' ? 'active' : ''}
                title="Globe"
                aria-label="Globe"
                onClick={() => setView('globe')}
              >
                <Globe2 size={17} />
              </button>
            </div>
            <button
              className={`compact-timer ${timer.running ? 'running' : ''}`}
              style={{ '--remaining': timer.running ? Math.max(0, 1 - timer.progress) : 0 } as React.CSSProperties}
              onClick={() => setTimerPopoverOpen(!timerPopoverOpen)}
            >
              <Clock3 size={18} /><time>{timer.display}</time>
              <span className="compact-separator" />
              <span onClick={(event) => { event.stopPropagation(); timer.toggle() }}>
                {timer.running ? <Pause size={16} /> : <Play size={16} />}
              </span>
            </button>
            {timerPopoverOpen && (
              <>
                <div className="popover-backdrop" onClick={() => setTimerPopoverOpen(false)} />
                <div className="timer-popover">
                  <p className="focus-minutes">You've been focusing for <strong>{focusMinutes} {focusMinutes === 1 ? 'minute' : 'minutes'}</strong></p>
                  <div className="timer-popover-main">
                    <button aria-label="Subtract five minutes" onClick={() => timer.adjust(-5)}><Minus size={16} /></button>
                    <time>{timer.display}</time>
                    <button aria-label="Add five minutes" onClick={() => timer.adjust(5)}><Plus size={16} /></button>
                    <button aria-label={timer.running ? 'Pause timer' : 'Start timer'} className="popover-play" onClick={timer.toggle}>
                      {timer.running ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
                    </button>
                  </div>
                  <div className="preset-row">
                    {[10, 25, 50].map((minutes) => (
                      <button
                        className={timer.durationMinutes === minutes ? 'selected' : ''}
                        key={minutes}
                        onClick={() => timer.setMinutes(minutes)}
                      >
                        {minutes} mins
                      </button>
                    ))}
                  </div>
                  <label className="timer-sound-toggle">
                    <span><Volume2 size={15} /> Play sound at end of timer</span>
                    <input
                      className="switch"
                      type="checkbox"
                      checked={timer.soundEnabled}
                      onChange={(event) => timer.setSoundEnabled(event.target.checked)}
                    />
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        <VideoGrid
          users={gridUsers}
          localStream={localDisplayStream}
          remoteStreams={remoteStreams}
          page={page}
          pageSize={pageSize}
          pinnedIds={pinnedIds}
          onPageChange={setPage}
          onSelectUser={selectUser}
          onTogglePin={togglePin}
          onHide={toggleHidden}
          onReport={reportUser}
        />

        <div className="call-controls">
          <button className="call-control mic-disabled" disabled title="Microphones are disabled"><MicOff /></button>
          <button className={`call-control ${camera.cameraOn ? '' : 'danger'}`} onClick={toggleCamera} title={camera.cameraOn ? 'Turn camera off' : 'Turn camera on'}>
            {camera.cameraOn ? <Video /> : <VideoOff />}
          </button>
          <button className={`call-control ${pipActive ? 'pip-active' : ''}`} disabled={!pipAvailable} onClick={togglePiP} title="Picture in picture"><PictureInPicture2 /></button>
          <button className="call-control leave" onClick={leaveRoom} title="Leave the room"><LogOut /></button>
        </div>

        <nav className="room-toolbar" aria-label="Room tools">
          <ToolbarButton active={activePanel === 'sounds'} icon={<Music2 />} label="Sounds" onClick={() => togglePanel('sounds')} />
          <ToolbarButton active={activePanel === 'intentions'} icon={<CheckSquare2 />} label="Intentions" onClick={() => togglePanel('intentions')} />
          <ToolbarButton active={activePanel === 'chat'} icon={<MessageCircle />} label="Chat" onClick={() => togglePanel('chat')} />
          <ToolbarButton active={activePanel === 'people'} icon={<Users />} label="People" onClick={() => togglePanel('people')} />
        </nav>

      </main>

      {activePanel === 'sounds' && <SoundsPanel onToast={showToast} onClose={() => setActivePanel(null)} />}
      {activePanel === 'intentions' && (
        <IntentionsPanel
          identity={identity}
          intentions={demoMode ? [...room.intentions, ...DEMO_INTENTIONS] : room.intentions}
          timer={timer}
          onCreate={room.createIntention}
          onUpdate={room.updateIntention}
          onReact={room.reactToIntention}
          onDelete={room.deleteIntention}
          onClose={() => setActivePanel(null)}
        />
      )}
      {activePanel === 'chat' && (
        <ChatPanel
          identity={identity}
          messages={room.messages}
          onSend={room.sendMessage}
          onReact={room.reactToMessage}
          onClose={() => setActivePanel(null)}
        />
      )}
      {activePanel === 'people' && (
        <PeoplePanel
          users={displayedUsers}
          selectedUser={selectedUser}
          currentUserId={identity.id}
          self={identity}
          hiddenIds={hiddenIds}
          onSelectUser={setSelectedUser}
          onSaveProfile={async (profile) => {
            if (!previewMode) {
              const updated = await auth.updateProfile(profile)
              if (updated) {
                room.updateProfile(updated)
                setSelectedUser((current) => current ? { ...current, ...updated } : current)
              }
              return
            }
            room.updateProfile({ ...profile, avatarUrl: profile.avatarUrl ?? identity.avatarUrl })
            setSelectedUser((current) => current ? { ...current, ...profile } : current)
          }}
          onUploadAvatar={async (file) => {
            if (previewMode) return
            const updated = await auth.uploadAvatar(file)
            room.updateProfile(updated)
            setSelectedUser((current) => current ? { ...current, avatarUrl: updated.avatarUrl } : current)
          }}
          onToggleHidden={toggleHidden}
          onReport={reportUser}
          onClose={() => { setActivePanel(null); setSelectedUser(null) }}
        />
      )}
      {view === 'globe' && joined && (
        <GlobePanel
          users={globeUsers}
          streamFor={(user) => user.id === identity?.id
            ? localDisplayStream
            : remoteStreams.get(user.id) ?? (user.socketId ? remoteStreams.get(user.socketId) : undefined)}
          onSelectUser={(user) => { setSelectedUser(user); setActivePanel('people') }}
        />
      )}

      {celebration !== null && (
        <div className="celebration-backdrop" role="dialog" aria-modal="true" onClick={() => setCelebration(null)}>
          <div className="celebration-card" onClick={(event) => event.stopPropagation()}>
            <button className="celebration-close" aria-label="Close" onClick={() => setCelebration(null)}><X /></button>
            <span className="celebration-badge"><SessionBadge sessions={celebration} /></span>
            <h3>{celebrationCopy(celebration).title}</h3>
            <p>{celebrationCopy(celebration).text}</p>
            <button className="celebration-cta" onClick={() => setCelebration(null)}>Keep going</button>
          </div>
        </div>
      )}

      {cameraPrompt && (
        <CameraOffDialog
          productName={import.meta.env.VITE_PRODUCT_NAME || 'Focus'}
          seconds={cameraPromptSeconds}
          onTurnOn={() => { camera.setEnabled(true); setCameraPrompt(false) }}
          onStayOff={() => setCameraPrompt(false)}
        />
      )}

      {settingsOpen && (
        <SettingsDialog
          stream={localDisplayStream}
          devices={camera.devices}
          deviceId={camera.deviceId}
          backgroundEffect={daily.backgroundEffect}
          effectsAvailable={!previewMode && daily.state === 'joined'}
          onSelectDevice={camera.selectDevice}
          onBackgroundEffect={daily.applyBackground}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  )
}
